import { useId, useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  alpha,
} from '@mui/material';
import { useTimeout } from 'usehooks-ts';
import { Trans, useTranslation } from 'react-i18next';
import { useNotificationsSettings } from 'features/notifications/hooks/useNotificationsSettings';
import { getLastPushNotificationTimestamp } from 'state/localStorage';
import { useOnboardingState } from 'state/useOnboardingState';
import { requestCriticalAlertsPermission } from 'shared/utils';
import { useReleaseFlag } from 'hooks/useReleaseFlags';
import { PushNotificationPreview } from 'features/notifications/components/PushNotificationPreview';
import { Capacitor } from '@capacitor/core';
import { getResponsiveFontSize } from 'theme/watchdutyTheme.utils';

const TWENTY_MINUTES_MS = 1000 * 60 * 20; // 20 minutes
const WAIT_DELAY_MS = 3000; // Wait for race condition where push notification handlers call setLastPushNotificationTimestamp

// Let text scale with system settings but cap it at explicit maxes
// so this compact CTA dialog can't overflow at large accessibility text sizes.
const TITLE_FONT_SIZE = getResponsiveFontSize('1.5rem', { maxSizePx: 30 });
const BASE_FONT_SIZE = getResponsiveFontSize('1rem', { maxSizePx: 20 });

const CriticalAlertsDialogDetails = (): JSX.Element | null => {
  const { t } = useTranslation();
  const reactId = useId();
  const titleId = `${reactId}-title`;
  const descriptionId = `${reactId}-description`;

  const { setCriticalAlertsRequested } = useOnboardingState();
  const { criticalSettings } = useNotificationsSettings();
  const { criticalAlertsStatus } = criticalSettings;

  const handleEnableCriticalAlerts = async (): Promise<void> => {
    setCriticalAlertsRequested(true);
    await requestCriticalAlertsPermission();
  };

  const showDialog = criticalAlertsStatus === 'notSupported';
  if (!showDialog) {
    return null;
  }

  return (
    <Dialog
      open
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      slotProps={{
        paper: { sx: { m: 2, borderRadius: 2, outline: 'none' } },
      }}
    >
      <DialogTitle
        id={titleId}
        sx={{
          fontWeight: 'fontWeightBold',
          textAlign: 'center',
          fontSize: TITLE_FONT_SIZE,
          lineHeight: 1.25,
        }}
      >
        {t('criticalAlertPermission.title')}
      </DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <PushNotificationPreview
          title={t('criticalAlertsDialog.alert.description')}
          body=""
          isCritical
          sx={{
            backgroundColor: (theme) => alpha(theme.palette.warning.main, 0.09),
            borderRadius: 2,
          }}
        />

        <Stack id={descriptionId} spacing={1}>
          <Typography sx={{ fontSize: BASE_FONT_SIZE }}>
            <Trans i18nKey="criticalAlertPermission.description.bypass">
              Watch Duty can alert you in critical situations even if your phone
              is <b>silenced</b> or in <b>Do Not Disturb</b>.
            </Trans>
          </Typography>

          <Typography sx={{ fontSize: BASE_FONT_SIZE }}>
            <Trans i18nKey="criticalAlertPermission.description.emergencyOnly">
              Critical Alerts are only used in <b>extreme situations</b> with
              <b>immediate threats</b> to life and safety.
            </Trans>
          </Typography>
        </Stack>

        <Button
          fullWidth
          size="large"
          onClick={handleEnableCriticalAlerts}
          aria-label={t('common.next')}
          sx={{ fontSize: BASE_FONT_SIZE }}
        >
          {t('common.next')}
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export const CriticalAlertsDialog = (): JSX.Element | null => {
  const [waitComplete, setWaitComplete] = useState(false);
  const isCriticalAlertsEnabled = useReleaseFlag('enable_critical_alerts');
  const { onboardCompleted, showTutorialSteps, criticalAlertsRequested } =
    useOnboardingState();

  useTimeout(() => setWaitComplete(true), WAIT_DELAY_MS);

  const lastPushNotificationTimestamp = getLastPushNotificationTimestamp();

  const showDialog =
    waitComplete &&
    Capacitor.getPlatform() === 'ios' &&
    isCriticalAlertsEnabled &&
    onboardCompleted &&
    !showTutorialSteps &&
    !criticalAlertsRequested &&
    Date.now() - lastPushNotificationTimestamp > TWENTY_MINUTES_MS;

  if (!showDialog) {
    return null;
  }

  // Mount CriticalAlertsDialogDetails after other conditions are checked so useNotificationsSettings
  // runs only when this dialog is shown. The onboarding notifications step also uses that hook;
  // if both mounted together they could race on push registration and leave onboarding stuck.
  return <CriticalAlertsDialogDetails />;
};
